import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import { useSelector } from "react-redux";
import { useTranslate, useTranslateFunction } from "lang/Translate";
import { DateTime, Duration } from "luxon";

// styles
import "./AircraftPopup.scoped.scss";

// common components
import SlidingTextButton from "components/common/SlidingTextButton/SlidingTextButton";
import CrewList from "components/common/CrewList/CrewList";

// components
import ProgressBar from "components/common/ProgressBar/ProgressBar";
import ChangeRegistration from "components/common/ChangeRegistration/ChangeRegistration";

// classes
import Flight from "classes/flight";
import AircraftActions from "classes/aircraftActions";

// custom hooks and utilities
import useRedirect, { routerOptions } from "hooks/useRedirect";
import useRemainingFlightTime from "hooks/useRemainingFlightTime";
import useAircraftFlight from "hooks/useAircraftFlight";
import getAircraftImage from "common/getAircraftImage";
import { useTabIdNew } from "hooks/useTabId";
import useAircraft from "hooks/useAircraft";
import useRemember from "hooks/useRemember";
import useColors from "hooks/useColors";

const AircraftPopup = forwardRef((props, ref) => {
	const ident = useTabIdNew("aircraft", { ...routerOptions.get("s"), isNumber: true });
	const aircraftIdent = useRemember(ident);
	const aircraftList = useSelector(state => state.aircraftList);

	const [img, setImg] = useState("");
	const [popupHeader, setPopupHeader] = useState("XX-XXXX");
	const [aircraftName, setAircraftName] = useState("");
	const [manufacturer, setManufacturer] = useState("");
	const [remainingBuildTime, setRemainingBuildTime] = useState(0); // sec
	const [color, darkColor] = useColors(aircraftIdent);

	const flightDetails = useAircraftFlight(aircraftIdent);
	const aircraft = useAircraft(aircraftIdent);
	const translate = useTranslateFunction();
	const redirect = useRedirect();

	const [remainingCleaningTime, setRemainingCleaningTime] = useState(0); // sec

	const getAircraftManufacturer = useCallback(
		id =>
			Object.entries(aircraftList.passenger).filter(([, value]) => {
				const containsAircraft = value.filter(aircraft => aircraft.id === id).length > 0;
				return containsAircraft;
			})[0],
		[aircraftList]
	);

	const goToAircraftDetails = () => {
		aircraft != null && (aircraft.showedInPopup = false);
		redirect(`aircraftIcon.fleet.overview.details~${aircraft.ident}`);
	};

	useImperativeHandle(ref, () => ({ subscribe: callback => callback({ color: darkColor }), color: darkColor }), [
		darkColor
	]);

	useEffect(() => {
		if (!aircraft) return;

		const aircraftManufacturer = getAircraftManufacturer(aircraft.id);
		const aircraftSpecs = aircraftManufacturer[1].find(a => a.id === aircraft.id);
		setManufacturer(aircraftSpecs.manufacturer || aircraftManufacturer[0]);
		setAircraftName(aircraftSpecs.name);
	}, [aircraft, getAircraftManufacturer]);

	useEffect(() => {
		if (!aircraft) return;
		if (aircraft.status === "cleaning") {
			setPopupHeader(translate("AIRCRAFT_IS_BEING_CLEANED", "AircraftPopup"));
			return;
		}

		if (aircraft.grounded) {
			setPopupHeader(translate("AIRCRAFT_GROUNDED", "AircraftPopup"));
			return;
		}

		if (aircraft.status === "delivery") {
			if (remainingBuildTime <= 0) {
				setPopupHeader(translate("DELIVERY_TEXT", "AircraftPopup"));
				return;
			}

			setPopupHeader(translate("BEING_BUILT_TEXT", "AircraftPopup"));
			return;
		}

		if (aircraft.status === "parked") {
			setPopupHeader(translate("PARKED", "AircraftPopup"));
			return;
		}

		setPopupHeader(Flight.get(aircraft.currentFlight)?.number || "XX-XXXX");
	}, [aircraft, remainingBuildTime, translate]);

	useEffect(() => {
		if (!aircraft?.id) return;
		const allAircraft = Object.values(aircraftList.passenger).reduce((acc, manufacturer, i) => {
			if (i === 0) return acc;
			return acc.concat(Object.values(manufacturer));
		}, []);

		setImg(getAircraftImage(allAircraft.find(a => a.id === aircraft.id)?.img_path));
	}, [aircraft?.id, aircraftList]);

	useEffect(() => {
		if (!aircraft) return;
		if (aircraft.status !== "delivery") {
			setRemainingBuildTime(0);
			return;
		}

		const interval = setInterval(() => setRemainingBuildTime(aircraft.getRemainingBuildTime()), 1000);
		return () => clearInterval(interval);
	}, [aircraft]);

	useEffect(() => {
		if (aircraft?.status !== "cleaning") return;

		const interval = setInterval(fn, 1000);

		function fn() {
			const remaining = aircraft.getRemainingCleaningTime();
			setRemainingCleaningTime(remaining);
			remaining <= 0 && clearInterval(interval);
		}

		fn();
		return () => clearInterval(interval);
	}, [aircraft, aircraft?.status]);

	// TODO: Store classes of the below element in an array.
	return (
		<div className={`container ${color}`}>
			<div className="header">
				<p className="flight_number" title={translate("FLIGHT_NO", "AircraftPopup")}>
					{popupHeader}
				</p>
			</div>

			<div className="img_container">
				<img src={img} alt="" />
			</div>

			<ChangeRegistration
				aircraft={aircraft}
				center={true}
				title={translate("AIRCRAFT_REGISTRATION", "AircraftPopup")}
				style={{ position: "relative", top: "-1.5rem" }}
				key={aircraft?.registration}
			/>

			{((remainingBuildTime > 0 && aircraft?.status === "delivery") || aircraft?.status === "cleaning") && (
				<div className="remaining_build_time">
					<div className="label">
						{aircraft?.status === "delivery"
							? translate("REMAINING_BUILD_TIME", "AircraftPopup")
							: translate("REMAINING_CLEANING_TIME", "AircraftPopup")}
					</div>

					{aircraft?.status === "delivery" &&
						Duration.fromObject({ seconds: remainingBuildTime }).toFormat("mm:ss") + " min"}
					{aircraft?.status === "cleaning" && `${remainingCleaningTime} s`}
				</div>
			)}

			{remainingBuildTime <= 0 && aircraft?.status !== "parked" && (
				<div className="route">
					<div className="from">{flightDetails?.originCode}</div>
					<i className="fas fa-plane" />
					<div className="to">{flightDetails?.destinationCode}</div>
				</div>
			)}

			{(aircraft?.status === "inflight" || (aircraft?.status === "delivery" && remainingBuildTime <= 0)) && (
				<FlightProgress aircraft={aircraft} flightDetails={flightDetails} />
			)}

			<AircraftActionButtonsTab aircraft={aircraft} remainingBuildTime={remainingBuildTime} />

			<Tab
				Icon={<i className="fa fa-plane moved" />}
				text={useTranslate({ component: "AircraftPopup", Tkey: "AIRCRAFT" })}
			>
				<div className="item">
					<div className="heading">{useTranslate({ component: "AircraftPopup", Tkey: "MANUFACTURER" })}</div>
					<div className="content">{manufacturer}</div>
				</div>

				<div className="item">
					<div className="heading">{useTranslate({ component: "AircraftPopup", Tkey: "MODEL" })}</div>
					<div className="content">{aircraftName}</div>
				</div>

				<div className="item">
					<div className="heading">{useTranslate({ component: "AircraftPopup", Tkey: "REGISTRATION" })}</div>
					<div className="content">{aircraft?.registration || "XX-(X)XXX"}</div>
				</div>

				{aircraft?.status !== "delivery" && (
					<div className="link" onClick={goToAircraftDetails}>
						<i className="fas fa-link" />
					</div>
				)}
			</Tab>

			<FlightData remainingBuildTime={remainingBuildTime} aircraft={aircraft} />
			<FlightInformation remainingBuildTime={remainingBuildTime} aircraft={aircraft} flightDetails={flightDetails} />
			<StaffInformation aircraft={aircraft} />
		</div>
	);
});

const AircraftActionButtonsTab = ({ aircraft, remainingBuildTime }) => {
	const translate = useTranslateFunction();
	const btnNum = new AircraftActions(aircraft?.ident).actions.length + +(remainingBuildTime >= 5) + 1;

	if (btnNum <= 1) {
		return <AircraftActionButtons aircraft={aircraft} remainingBuildTime={remainingBuildTime} />;
	}

	return (
		<Tab
			text={translate("ACTION_BUTTONS_TEXT", "AircraftPopup")}
			Icon={<i className="fas fa-circle-dot" />}
			lessSpace={true}
		>
			<AircraftActionButtons aircraft={aircraft} remainingBuildTime={remainingBuildTime} />
		</Tab>
	);
};

const AircraftActionButtons = ({ aircraft, remainingBuildTime }) => {
	const aircraftActions = new AircraftActions(aircraft?.ident);

	// translations
	const CANCEL_ORDER = useTranslate({ component: "AircraftPopup", Tkey: "CANCEL_ORDER" });

	if (remainingBuildTime >= 5 || aircraftActions.actions.length > 0) {
		return (
			<div className="buttons">
				{aircraftActions.getJsx("dark", false, "big")}

				{remainingBuildTime >= 5 && (
					<SlidingTextButton
						text={CANCEL_ORDER}
						icon={<i className="fas fa-times" />}
						background={
							aircraft?.status === "delivery" ? "purple" : aircraft?.status === "landed" ? "green" : "darkBlue"
						}
					/>
				)}
			</div>
		);
	}

	return null;
};

const FlightProgress = ({ aircraft, flightDetails }) => {
	const flightTime = flightDetails?.flightTime; // sec
	const [flown, setFlown] = useState(0); // sec
	const [flownDistance, setFlownDistance] = useState(0); // km
	const remainingFlightTime = useRemainingFlightTime(aircraft?.ident);
	const route = useSelector(state => state.routes).find(r => r.aircraft.includes(aircraft?.ident));

	// compute flown
	useEffect(() => {
		const callback = () => {
			if (!aircraft || (aircraft?.status !== "delivery" && aircraft?.status !== "inflight")) return;
			if (!aircraft.departed && aircraft.status === "delivery") return;

			if (!flightTime) return;
			const departed = aircraft.departed;
			let traveledTime = DateTime.utc().toSeconds() - departed;
			if (traveledTime < 0) traveledTime = 0;
			if (traveledTime > flightTime) traveledTime = flightTime;
			setFlown(traveledTime);
		};

		callback();
		const interval = setInterval(callback, 1000);
		return () => clearInterval(interval);
	}, [aircraft, flightTime]);

	// compute flownDistance
	useEffect(() => {
		if (!route || !aircraft) return;
		const speed = aircraft.specs.speed[0]; // km/h
		const distance = (flown / 3600) * (speed * 1.5); // km
		setFlownDistance(Math.round(distance) || 0);
	}, [flown, route, aircraft]);

	return (
		<div className="progress">
			<ProgressBar
				value={(flown / flightTime) * 100}
				completedColor={aircraft?.status === "delivery" ? "pink" : null}
				style={{ width: "90%", margin: "0 auto .5rem", overflow: "visible" }}
			>
				<i
					className="fas fa-plane icon"
					style={{
						position: "absolute",
						right: "0",
						transform: "translate(50%, -39%)",
						fontSize: "1.2rem",
						color: aircraft?.status === "delivery" ? "purple" : "hsl(168, 97%, 31%)"
					}}
				/>
			</ProgressBar>

			<div className="times">
				<div className="time">{Duration.fromObject({ seconds: flown || 0 }).toFormat("hh:mm")} h</div>
				<div className="time">{Duration.fromObject({ milliseconds: remainingFlightTime }).toFormat("hh:mm")} h</div>
				<div className="time">{flightDetails?.formattedFlightTime}</div>
			</div>

			<div className="distances">
				<div className="distance">{flownDistance} km</div>
				<div className="distance">{flightDetails?.distance} km</div>
			</div>
		</div>
	);
};

const FlightData = ({ remainingBuildTime, aircraft }) => {
	const FLIGHT_DATA = useTranslate({ component: "AircraftPopup", Tkey: "FLIGHT_DATA" });
	const HEADING = useTranslate({ component: "AircraftPopup", Tkey: "HEADING" });
	const SPEED = useTranslate({ component: "AircraftPopup", Tkey: "SPEED" });
	const ALTITUDE = useTranslate({ component: "AircraftPopup", Tkey: "ALTITUDE" });

	if (!aircraft) return null;
	if (remainingBuildTime > 0) return null;
	if (aircraft?.status !== "inflight" || aircraft?.status !== "delivery") return null;

	return (
		<Tab Icon={<i className="fa fa-chart-area moved" />} text={FLIGHT_DATA} className="flight_data_tab">
			<div className="flex flex--columns flex--space_between equal_columns">
				<div className="item no_space">
					<div className="heading">{HEADING}</div>
					<div className="content">N/A</div>
				</div>
				<div className="item no_space">
					<div className="heading">{SPEED}</div>
					<div className="content">N/A</div>
				</div>
				<div className="item no_space">
					<div className="heading">{ALTITUDE}</div>
					<div className="content">N/A</div>
				</div>
			</div>
		</Tab>
	);
};

const FlightInformation = ({ remainingBuildTime, aircraft, flightDetails }) => {
	const translate = useTranslateFunction();

	if (!aircraft || aircraft?.status === "parked" || !flightDetails) return null;
	return (
		remainingBuildTime <= 0 && (
			<Tab Icon={<i className="fa fa-route" />} text={translate("ROUTE", "AircraftPopup")}>
				<div className="item">
					<div className="header">{translate("FROM", "AircraftPopup")}</div>
					<div className="content">{flightDetails.origin}</div>
				</div>
				<div className="item">
					<div className="header">{translate("TO", "AircraftPopup")}</div>
					<div className="content">{flightDetails.destination}</div>
				</div>
				<div className="item">
					<div className="header">{translate("DISTANCE", "AircraftPopup")}</div>
					<div className="content">{flightDetails.distance} km</div>
				</div>
				<div className="item">
					<div className="header">{translate("FLIGHT_TIME", "AircraftPopup")}</div>
					<div className="content">{flightDetails.formattedFlightTime}</div>
				</div>
			</Tab>
		)
	);
};

const StaffInformation = ({ aircraft }) => {
	const translate = useTranslateFunction("AircraftPopup");

	if (!aircraft) return null;
	return (
		<Tab
			Icon={<i className="fas fa-users" />}
			text={translate("CREW")}
			defaultState={aircraft.status !== "delivery"}
			className="crew"
		>
			<CrewList aircraftIdent={aircraft.ident} />
		</Tab>
	);
};

const Tab = ({ text, Icon = <></>, children, className = "", lessSpace = false, defaultState = true }) => {
	const [open, setOpen] = useState(defaultState);

	return (
		<div className={`tab ${className}`}>
			<div className="heading" onClick={() => setOpen(prev => !prev)}>
				{Icon} {text}
				<i className={open ? "fas fa-angle-right rotated" : "fas fa-angle-right"} />
			</div>
			<div className={open ? "content open" : "content"}>
				{!lessSpace && <div className="space" />}
				{children}
				<div className="space" />
			</div>
		</div>
	);
};

export default AircraftPopup;
